import { useEffect } from "react";

const SITE_NAME = "GuestIntel";
const SITE_TAGLINE = "Know Ahead.";
const SITE_DEFAULT_DESC =
  "GuestIntel — the hospitality intelligence network. Verified incident records, privacy-preserving cross-tenant matching, and an evidence-based appeal process.";
const SITE_ORIGIN = "https://guestintel.net";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/desktop-logo.png`;

/** Upsert <meta name="..."> or <meta property="..."> tag on document.head. */
function setMeta(attr, key, value) {
  if (!value) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return el;
}

/** Upsert <link rel="canonical"> tag. */
function setCanonical(href) {
  if (!href) return null;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

/**
 * Dynamically set per-route SEO metadata (title, description, OG/Twitter, canonical).
 * Reverts to the previous title on unmount so navigating away doesn't leave stale state.
 *
 * Notes for future SSR/prerender: most social crawlers (LinkedIn, Twitter, Facebook)
 * do NOT execute JS, so the OG tags injected here are only reliably picked up by
 * Googlebot (which renders JS). To unlock perfect social cards, prerender these
 * routes at build time or add SSR — tracked as a future enhancement.
 */
export default function useSeo({ title, description, path, image, type = "website" } = {}) {
  useEffect(() => {
    const prevTitle = document.title;

    const fullTitle = title
      ? `${title} · ${SITE_NAME}`
      : `${SITE_NAME} · ${SITE_TAGLINE}`;
    const desc = description || SITE_DEFAULT_DESC;
    const url = path ? `${SITE_ORIGIN}${path}` : SITE_ORIGIN;
    const og = image || DEFAULT_OG_IMAGE;

    document.title = fullTitle;

    setMeta("name", "description", desc);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:image", og);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", og);
    setCanonical(url);

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, path, image, type]);
}
